import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Banknote,
  Eye,
  FileText,
  Package,
  RefreshCw,
  ShoppingBag,
  Store,
  TrendingUp,
  Truck,
  UserPlus,
  CalendarClock,
} from 'lucide-react';
import { biAPI } from '../../services/api';
import { chartAxisTick, chartTooltipStyle, formatNpr, formatNumber, formatPct } from './biTheme';
import BiFilters from './BiFilters';
import BiKpiCard from './BiKpiCard';
import ScrollableBarList from './ScrollableBarList';
import { useBiVendorId } from './useBiVendorId';
import useDebouncedValue from './useDebouncedValue';

const PURPLE = '#8b5cf6';
const BLUE = '#3b82f6';
const DONUT_COLORS = ['#8b5cf6', '#3b82f6', '#22c55e', '#f59e0b', '#f43f5e', '#14b8a6', '#6366f1', '#64748b'];

const DEFAULT_METRICS = [
  { key: 'totalSales', label: 'Total Sales', format: 'npr' },
  { key: 'totalOrders', label: 'Total Orders Completed', format: 'number' },
  { key: 'aov', label: 'AOV', format: 'npr' },
  { key: 'avgOrders', label: 'Avg Orders', format: 'number' },
  { key: 'avgSales', label: 'Avg Sales', format: 'npr' },
  { key: 'basketSize', label: 'Basket size', format: 'number' },
  { key: 'totalQty', label: 'Total Qty', format: 'number' },
  { key: 'totalUsers', label: 'Total Users', format: 'number' },
];

const defaultTo = () => new Date().toISOString().slice(0, 10);
const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 13);
  return d.toISOString().slice(0, 10);
};

const heatmapColor = (value, max) => {
  if (!max || !value) return '#f8fafc';
  const t = Math.min(1, value / max);
  const lightness = 94 - Math.round(t * 50);
  return `hsl(258 90% ${lightness}%)`;
};

const formatMetricValue = (value, format) => {
  if (format === 'npr') return formatNpr(value);
  if (format === 'pct') return formatPct(value);
  return formatNumber(Number(value || 0) % 1 === 0 ? value : Number(Number(value).toFixed(2)));
};

const daySpan = (from, to) => {
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
};

/** Real trend values only — no decorative / seeded sparklines. */
const buildSparkPoints = (points, key) => {
  const rows = (points || [])
    .map((row) => {
      if (key === 'aov') {
        const sales = Number(row.sales ?? row.value ?? 0);
        const orders = Number(row.orders ?? 0);
        return orders > 0 ? sales / orders : null;
      }
      const value = Number(row[key] ?? row.value ?? 0);
      return Number.isFinite(value) ? value : null;
    })
    .filter((v) => v != null && Number.isFinite(v));

  return rows.length >= 2 ? rows.slice(-14) : rows;
};

const MiniStat = ({ icon: Icon, label, value, hint, color = BLUE }) => (
  <div className="relative flex min-h-0 items-center gap-2.5 overflow-hidden rounded-xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 px-3 py-2.5 shadow-[0_6px_18px_rgba(15,23,42,0.04)]">
    <span
      className="absolute inset-y-0 left-0 w-1"
      style={{ background: `linear-gradient(180deg, ${color}, ${color}88)` }}
      aria-hidden
    />
    <div
      className="ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
      style={{ backgroundColor: `${color}18`, color }}
    >
      <Icon className="h-4 w-4" strokeWidth={2.25} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate text-[clamp(0.78rem,0.9vw,0.85rem)] font-medium text-slate-500">{label}</p>
      <p className="truncate text-[clamp(1.3rem,1.55vw,1.65rem)] font-bold leading-tight tracking-tight text-slate-900">
        {value}
      </p>
      {hint ? (
        <p className="truncate text-[clamp(0.72rem,0.85vw,0.8rem)] font-medium text-slate-400">{hint}</p>
      ) : null}
    </div>
  </div>
);

const StoreShareCompare = ({ rows, metricLabel, format }) => {
  if (!rows?.length) {
    return <p className="text-sm text-gray-500">No store data in this range.</p>;
  }

  const top = rows.slice(0, 2);
  const a = top[0];
  const b = top[1];
  const aPct = Number(a?.value || 0);
  const bPct = Number(b?.value || 0);
  const totalPct = Math.max(aPct + bPct, 0.0001);
  const aShare = (aPct / totalPct) * 100;
  const bShare = b ? (bPct / totalPct) * 100 : 0;

  if (!b) {
    return (
      <div className="flex h-full flex-col justify-center gap-2">
        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-[clamp(1.05rem,1.2vw,1.15rem)] font-semibold text-slate-800">{a.name}</p>
            <p className="text-[clamp(0.78rem,0.9vw,0.85rem)] text-slate-500">
              {formatMetricValue(a.metricValue, format)} · {metricLabel}
            </p>
          </div>
          <p className="text-[clamp(1.55rem,2vw,2rem)] font-bold tabular-nums text-slate-900">
            {aPct.toFixed(0)}%
          </p>
        </div>
        <div className="h-3 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full" style={{ width: '100%', background: a.color || PURPLE }} />
        </div>
        <p className="text-[clamp(0.92rem,1.05vw,1rem)] text-slate-400">Only one store in current filter</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col justify-center gap-2.5">
      <div className="grid grid-cols-2 gap-3">
        <div className="min-w-0 rounded-lg bg-violet-50/80 px-2.5 py-2">
          <p className="truncate text-[clamp(0.82rem,0.95vw,0.9rem)] font-semibold text-violet-900">{a.name}</p>
          <p className="mt-0.5 text-[clamp(1.45rem,1.9vw,1.85rem)] font-bold leading-none tabular-nums text-violet-700">
            {aShare.toFixed(0)}%
          </p>
          <p className="mt-1 truncate text-[clamp(0.92rem,1.05vw,1rem)] text-violet-600/80">
            {formatMetricValue(a.metricValue, format)}
          </p>
        </div>
        <div className="min-w-0 rounded-lg bg-sky-50/80 px-2.5 py-2 text-right">
          <p className="truncate text-[clamp(0.82rem,0.95vw,0.9rem)] font-semibold text-sky-900">{b.name}</p>
          <p className="mt-0.5 text-[clamp(1.45rem,1.9vw,1.85rem)] font-bold leading-none tabular-nums text-sky-700">
            {bShare.toFixed(0)}%
          </p>
          <p className="mt-1 truncate text-[clamp(0.92rem,1.05vw,1rem)] text-sky-600/80">
            {formatMetricValue(b.metricValue, format)}
          </p>
        </div>
      </div>
      <div className="relative h-4 overflow-hidden rounded-full bg-slate-100 shadow-inner">
        <div
          className="absolute inset-y-0 left-0 rounded-l-full"
          style={{ width: `${aShare}%`, background: `linear-gradient(90deg, ${PURPLE}, #a78bfa)` }}
        />
        <div
          className="absolute inset-y-0 right-0 rounded-r-full"
          style={{ width: `${bShare}%`, background: `linear-gradient(90deg, #7dd3fc, ${BLUE})` }}
        />
      </div>
      <div className="flex items-center justify-between text-[clamp(0.72rem,0.85vw,0.8rem)] font-semibold uppercase tracking-wide text-slate-400">
        <span>Store A</span>
        <span>vs</span>
        <span>Store B</span>
      </div>
      {rows.length > 2 ? (
        <ul className="mt-0.5 space-y-1 border-t border-slate-100 pt-1.5">
          {rows.slice(2, 5).map((row) => (
            <li key={row.name} className="flex items-center justify-between gap-2 text-[clamp(0.78rem,0.92vw,0.88rem)]">
              <span className="truncate text-slate-600">{row.name}</span>
              <span className="shrink-0 font-semibold tabular-nums text-slate-800">{Number(row.value || 0).toFixed(0)}%</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

const Panel = ({ title, action, children, className = '' }) => (
  <div className={`rounded-xl border border-slate-100 bg-white p-[clamp(0.65rem,1vw,1rem)] shadow-[0_6px_18px_rgba(15,23,42,0.04)] ${className}`}>
    <div className="mb-2 flex min-h-[1.4rem] flex-wrap items-center justify-between gap-2">
      <h3 className="text-[clamp(0.95rem,1.1vw,1.05rem)] font-semibold text-slate-900">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const CHART_H = 'h-[clamp(11.5rem,22vh,15.5rem)]';
const DEEP_PANEL_H = 'h-[clamp(20rem,40vh,30rem)]';

const OverviewDashboard = () => {
  const { vendorId } = useBiVendorId();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [weekdays, setWeekdays] = useState([]);
  const [salesGrain, setSalesGrain] = useState('daily');
  const [peakMode, setPeakMode] = useState('average');
  const [salesMetric, setSalesMetric] = useState('totalSales');
  const debouncedFrom = useDebouncedValue(from, 450);
  const debouncedTo = useDebouncedValue(to, 450);
  const debouncedWeekdays = useDebouncedValue(weekdays, 450);

  const queryParams = useMemo(() => ({
    vendorId,
    from: debouncedFrom,
    to: debouncedTo,
    salesGrain,
    peakMode,
    salesMetric,
    weekdays: debouncedWeekdays.join(','),
  }), [vendorId, debouncedFrom, debouncedTo, salesGrain, peakMode, salesMetric, debouncedWeekdays]);

  const reportQueryOpts = {
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  };

  const {
    data: coreData,
    isLoading,
    isError,
    error,
    refetch: refetchCore,
    isFetching: isFetchingCore,
  } = useQuery(
    ['bi-overview', 'core', queryParams],
    async () => {
      const response = await biAPI.getOverview({ ...queryParams, section: 'core' });
      return response.data?.data || response.data;
    },
    reportQueryOpts
  );

  const {
    data: extrasData,
    refetch: refetchExtras,
    isFetching: isFetchingExtras,
  } = useQuery(
    ['bi-overview', 'extras', queryParams],
    async () => {
      const response = await biAPI.getOverview({ ...queryParams, section: 'extras' });
      return response.data?.data || response.data;
    },
    {
      ...reportQueryOpts,
      enabled: Boolean(coreData) && !isError,
    }
  );

  const data = useMemo(() => {
    if (!coreData) return null;
    if (!extrasData) return coreData;
    return {
      ...coreData,
      kpis: {
        ...coreData.kpis,
        deltas: extrasData.kpis?.deltas || coreData.kpis?.deltas || {},
      },
      peakHours: extrasData.peakHours?.points?.length ? extrasData.peakHours : coreData.peakHours,
      storeShare: extrasData.storeShare?.length ? extrasData.storeShare : coreData.storeShare,
      weeklyHeatmap: extrasData.weeklyHeatmap?.weeks?.length
        ? extrasData.weeklyHeatmap
        : coreData.weeklyHeatmap,
      salesByLocation: extrasData.salesByLocation?.length
        ? extrasData.salesByLocation
        : coreData.salesByLocation,
      salesByProduct: extrasData.salesByProduct?.length
        ? extrasData.salesByProduct
        : coreData.salesByProduct,
    };
  }, [coreData, extrasData]);

  const refetch = () => {
    refetchCore();
    refetchExtras();
  };
  const isFetching = isFetchingCore || isFetchingExtras;
  const extrasLoading = Boolean(coreData) && !extrasData && isFetchingExtras;

  const kpis = data?.kpis || {};
  const kpiDeltas = kpis.deltas || {};
  const salesTrend = data?.salesTrend || { points: [], seriesKeys: ['value'] };
  const peakHours = data?.peakHours || { points: [], seriesKeys: ['value'] };
  const categoryShare = data?.categoryShare || [];
  const storeShare = data?.storeShare || [];
  const metricOptions = data?.salesMetricOptions?.length ? data.salesMetricOptions : DEFAULT_METRICS;
  const activeMetric = metricOptions.find((item) => item.key === salesMetric) || metricOptions[0];
  const heatmap = data?.weeklyHeatmap || {
    weeks: [],
    weekdays: [],
    cells: {},
    rowTotals: {},
    colTotals: {},
    rowAverages: {},
    colAverages: {},
    grandTotal: 0,
    grandAverage: 0,
  };
  const salesByLocation = data?.salesByLocation || [];
  const salesByCategory = data?.salesByCategory || [];
  const salesByProduct = data?.salesByProduct || [];

  const heatmapWeeks = useMemo(() => {
    const weeks = heatmap.weeks || [];
    if (!weeks.length) return [];
    if (typeof weeks[0] === 'object') return weeks;
    // backward compat: plain week numbers
    return weeks.map((week) => ({
      key: String(week),
      year: '',
      week: Number(week),
      label: `W${String(week).padStart(2, '0')}`,
    }));
  }, [heatmap.weeks]);

  const heatmapMax = useMemo(() => {
    const values = Object.values(heatmap.cells || {}).map(Number);
    return Math.max(...values, 0);
  }, [heatmap]);

  const days = daySpan(from, to);
  const avgOrdersPerDay = Number(kpis.totalOrders || 0) / days;
  const qtyTotal = (salesTrend.points || []).reduce(
    (sum, point) => sum + Number(point.qty || point.totalQty || 0),
    0
  );
  const avgItemsPerOrder = Number(kpis.totalOrders) ? qtyTotal / Number(kpis.totalOrders) : 0;

  const sparkSales = useMemo(() => buildSparkPoints(salesTrend.points, 'sales'), [salesTrend.points]);
  const sparkOrders = useMemo(() => buildSparkPoints(salesTrend.points, 'orders'), [salesTrend.points]);
  const sparkAov = useMemo(() => buildSparkPoints(salesTrend.points, 'aov'), [salesTrend.points]);
  const sparkMetric = useMemo(() => buildSparkPoints(salesTrend.points, 'value'), [salesTrend.points]);

  const peakChartPoints = useMemo(() => {
    const points = peakHours.points || [];
    const keys = peakHours.seriesKeys?.length ? peakHours.seriesKeys : ['value'];
    if (keys.length === 1 && keys[0] === 'value') return points;
    return points.map((point) => {
      const vals = keys.map((key) => Number(point[key] || 0));
      const avg = vals.length ? vals.reduce((sum, n) => sum + n, 0) / vals.length : 0;
      return { ...point, value: Number(avg.toFixed(2)) };
    });
  }, [peakHours.points, peakHours.seriesKeys]);

  const peakHighlight = useMemo(() => {
    if (!peakChartPoints.length) return null;
    return peakChartPoints.reduce((best, point) => {
      const bestVal = Number(best.value ?? best.orders ?? 0);
      const pointVal = Number(point.value ?? point.orders ?? 0);
      return pointVal > bestVal ? point : best;
    }, peakChartPoints[0]);
  }, [peakChartPoints]);

  const donutData = useMemo(() => {
    if (categoryShare.length) {
      return categoryShare.map((entry, index) => ({
        ...entry,
        color: entry.color || DONUT_COLORS[index % DONUT_COLORS.length],
      }));
    }
    return [];
  }, [categoryShare]);

  const storeDonutData = useMemo(() => {
    if (!storeShare.length) return [];
    return storeShare.map((entry, index) => ({
      name: entry.name,
      value: Number(entry.share || 0),
      metricValue: Number(entry.value || 0),
      color: entry.color || DONUT_COLORS[index % DONUT_COLORS.length],
    }));
  }, [storeShare]);

  const chartPoints = useMemo(() => {
    const points = salesTrend.points || [];
    if (!points.length) return [];
    // Recompute `value` from raw totals so metric switches never show stale / zero series.
    const metric = activeMetric?.key || salesMetric;
    return points.map((point) => {
      const sales = Number(point.sales ?? 0);
      const orders = Number(point.orders ?? 0);
      const qty = Number(point.qty ?? point.totalQty ?? 0);
      const users = Number(point.users ?? 0);
      const dayCount = Number(point.dayCount ?? 1) || 1;
      const hasRaw = point.sales != null || point.orders != null || point.qty != null || point.users != null;
      if (!hasRaw && point.value != null) return point;
      let value = Number(point.value ?? 0);
      if (metric === 'totalOrders') value = orders;
      else if (metric === 'aov') value = orders > 0 ? sales / orders : 0;
      else if (metric === 'avgOrders') value = orders / dayCount;
      else if (metric === 'avgSales') value = sales / dayCount;
      else if (metric === 'basketSize') value = orders > 0 ? qty / orders : 0;
      else if (metric === 'totalQty') value = qty;
      else if (metric === 'totalUsers') value = users;
      else if (metric === 'totalSales') value = sales;
      return { ...point, value };
    });
  }, [salesTrend.points, activeMetric?.key, salesMetric]);

  const locationRows = useMemo(() => {
    if (!salesByLocation.length) return [];
    return salesByLocation.map((row) => ({
      ...row,
      name: row.name,
      value: Number(row.value ?? row.sales ?? 0),
    }));
  }, [salesByLocation]);

  const categoryBarRows = useMemo(
    () => salesByCategory.map((row) => ({
      name: row.name,
      value: Number(row.value ?? row.sales ?? 0),
    })),
    [salesByCategory]
  );

  const productBarRows = useMemo(
    () => salesByProduct.map((row) => ({
      name: row.name,
      value: Number(row.value ?? row.sales ?? 0),
    })),
    [salesByProduct]
  );
  const yTickFormatter = (value) => {
    if (activeMetric?.format === 'npr') {
      if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
      if (Math.abs(value) >= 1000) return `${Math.round(value / 1000)}k`;
    }
    return formatNumber(Number(value || 0) % 1 === 0 ? value : Number(Number(value).toFixed(1)));
  };

  const trendTotal = useMemo(() => {
    const keys = salesTrend.seriesKeys?.length ? salesTrend.seriesKeys : ['value'];
    const sum = (chartPoints || []).reduce((acc, point) => {
      if (keys.length === 1 && keys[0] === 'value') {
        return acc + Number(point.value ?? 0);
      }
      return acc + keys.reduce((s, key) => s + Number(point[key] || 0), 0);
    }, 0);
    return formatMetricValue(sum, activeMetric?.format);
  }, [chartPoints, salesTrend.seriesKeys, activeMetric?.format]);

  const newCustomers = Number(kpis.newCustomers || 0);
  const returningCustomers = Number(kpis.returningCustomers || 0);
  const formatHeatValue = (value) => formatMetricValue(value, activeMetric?.format);

  const TrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div
        style={{ ...chartTooltipStyle, whiteSpace: 'nowrap' }}
        className="px-3 py-2 shadow-md"
      >
        <p className="mb-1 text-xs font-medium text-slate-500">{label}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey} className="whitespace-nowrap text-sm font-semibold text-slate-900">
            <span className="font-medium text-slate-600">
              {entry.name === 'value' ? (activeMetric?.label || 'Value') : entry.name}
              {': '}
            </span>
            {formatMetricValue(entry.value, activeMetric?.format)}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h2 className="text-[clamp(1.15rem,1.4vw,1.4rem)] font-bold text-gray-900">Executive Operations & Sales</h2>
        <BiFilters
          inline
          showStore={false}
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          weekdays={weekdays}
          onWeekdaysChange={setWeekdays}
          onRefresh={() => refetch()}
          refreshing={isFetching}
        />
      </div>

      {isLoading ? <div className="card text-gray-500">Loading sales metrics…</div> : null}
      {extrasLoading ? (
        <div className="text-sm text-slate-500">Loading charts & comparisons…</div>
      ) : null}
      {isError ? (
        <div className="card border-rose-200 bg-rose-50 text-rose-700">
          Failed to load overview: {error?.response?.data?.message || error?.message || 'Unknown error'}
        </div>
      ) : null}

      {!isLoading && !isError ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <BiKpiCard
              icon={Banknote}
              label="Net Sales"
              value={formatNpr(kpis.netSales)}
              deltas={kpiDeltas.netSales}
              color="#a855f7"
              sparkValues={sparkSales}
              waveVariant={0}
            />
            <BiKpiCard
              icon={FileText}
              label="Total Orders Completed"
              value={formatNumber(kpis.totalOrders)}
              deltas={kpiDeltas.totalOrders}
              color="#3b82f6"
              sparkValues={sparkOrders}
              waveVariant={1}
            />
            <BiKpiCard
              icon={TrendingUp}
              label="AOV"
              value={formatNpr(kpis.aovNpr)}
              deltas={kpiDeltas.aovNpr}
              color="#f59e0b"
              sparkValues={sparkAov}
              waveVariant={2}
            />
            <BiKpiCard
              icon={Truck}
              label="Fulfillment"
              value={formatPct(kpis.fulfillmentRate)}
              deltas={kpiDeltas.fulfillmentRate}
              sub={Number(kpis.fulfillmentRate) >= 99 ? 'On time' : undefined}
              color="#14b8b0"
              sparkValues={sparkOrders}
              waveVariant={1}
            />
            <BiKpiCard
              icon={Eye}
              label="View → Order"
              value={formatPct(kpis.cartConversion)}
              deltas={kpiDeltas.cartConversion}
              color="#f43f5e"
              sparkValues={sparkMetric}
              waveVariant={0}
            />
            <BiKpiCard
              icon={ShoppingBag}
              label="Total Orders Created"
              value={formatNumber(kpis.placedOrders || 0)}
              deltas={kpiDeltas.placedOrders}
              color="#06b6d4"
              sparkValues={sparkOrders}
              waveVariant={2}
            />
          </div>

          <div className="grid grid-cols-12 gap-[clamp(0.5rem,0.8vw,0.85rem)]">
            <Panel
              className="col-span-8"
              title="Sales Trend (Daily)"
              action={(
                <div className="flex items-center gap-1.5">
                  <span className="whitespace-nowrap text-[clamp(0.8rem,1vw,1rem)] font-bold tabular-nums text-slate-900">
                    {trendTotal}
                  </span>
                  <select
                    value={salesGrain}
                    onChange={(e) => setSalesGrain(e.target.value)}
                    className="input py-1 text-sm"
                    aria-label="Sales view"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="weekday">Weekday × week no.</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                  <select
                    value={salesMetric}
                    onChange={(e) => setSalesMetric(e.target.value)}
                    className="input py-1 text-sm"
                    aria-label="Sales metric"
                  >
                    {metricOptions.map((option) => (
                      <option key={option.key} value={option.key}>{option.label}</option>
                    ))}
                  </select>
                </div>
              )}
            >
              <div className={`${CHART_H} min-w-0`}>
                {chartPoints.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartPoints} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="biPurpleBlueFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={PURPLE} stopOpacity={0.38} />
                          <stop offset="55%" stopColor={BLUE} stopOpacity={0.14} />
                          <stop offset="100%" stopColor={BLUE} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="4 4" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="label" stroke="#94a3b8" tick={chartAxisTick} axisLine={false} tickLine={false} />
                      <YAxis stroke="#94a3b8" tick={chartAxisTick} tickFormatter={yTickFormatter} axisLine={false} tickLine={false} width={52} />
                      <Tooltip content={<TrendTooltip />} />
                      {(salesTrend.seriesKeys || ['value']).map((key) => (
                        <Area
                          key={key}
                          type="monotone"
                          dataKey={key}
                          name={key === 'value' ? activeMetric?.label : key}
                          stroke={BLUE}
                          fill="url(#biPurpleBlueFill)"
                          strokeWidth={2.75}
                          dot={{ r: 4, fill: BLUE, strokeWidth: 0 }}
                          activeDot={{ r: 5, fill: PURPLE }}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="flex h-full items-center text-sm text-gray-500">No trend points in this range.</p>
                )}
              </div>
            </Panel>

            <Panel className="col-span-4" title={`Category Share · ${activeMetric?.label || 'Sales'}`}>
              <div className={`flex ${CHART_H} items-center gap-2`}>
                {donutData.length ? (
                  <>
                    <div className="relative h-full w-[55%] min-w-[140px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={donutData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius="48%"
                            outerRadius="78%"
                            paddingAngle={2}
                            stroke="#fff"
                            strokeWidth={3}
                          >
                            {donutData.map((entry) => (
                              <Cell key={entry.name} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={chartTooltipStyle}
                            formatter={(value, _n, item) => [
                              `${value}% · ${formatMetricValue(item.payload.metricValue ?? item.payload.sales, activeMetric?.format)}`,
                              item.payload.name,
                            ]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-sm font-medium text-slate-500">Total</span>
                        <span className="text-lg font-bold text-slate-900">100%</span>
                      </div>
                    </div>
                    <ul className="flex h-full flex-1 flex-col justify-center gap-1.5 overflow-auto pr-1 text-[clamp(0.82rem,0.95vw,0.9rem)]">
                      {donutData.map((entry) => (
                        <li key={entry.name} className="flex items-center justify-between gap-1.5">
                          <span className="flex min-w-0 items-center gap-1.5 text-slate-700">
                            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
                            <span className="truncate">{entry.name}</span>
                          </span>
                          <span className="shrink-0 font-semibold tabular-nums text-slate-900">{entry.value}%</span>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="text-sm text-gray-500">No category data in this range.</p>
                )}
              </div>
            </Panel>
          </div>

          <div className="grid grid-cols-12 items-stretch gap-[clamp(0.4rem,0.7vw,0.65rem)]">
            <div className="col-span-7 grid grid-cols-2 gap-[clamp(0.4rem,0.7vw,0.65rem)] xl:grid-cols-4">
              <MiniStat
                icon={CalendarClock}
                label="Avg Orders / Day"
                value={formatNumber(Number(avgOrdersPerDay.toFixed(1)))}
                hint="Selected date range"
                color="#0ea5e9"
              />
              <MiniStat
                icon={Package}
                label="Avg Items / Order"
                value={formatNumber(Number((avgItemsPerOrder || 0).toFixed(1)))}
                hint="Basket depth"
                color="#8b5cf6"
              />
              <MiniStat
                icon={UserPlus}
                label="New Customers"
                value={formatNumber(newCustomers)}
                hint="No prior OneMart order before range"
                color="#10b981"
              />
              <MiniStat
                icon={RefreshCw}
                label="Returning Customers"
                value={formatNumber(returningCustomers)}
                hint="Prior OneMart order + ordered in range"
                color="#f59e0b"
              />
            </div>
            <Panel
              className="col-span-5"
              title={`Store Share · ${activeMetric?.label || 'Sales'}`}
              action={(
                <span className="inline-flex items-center gap-1 text-[clamp(0.78rem,0.92vw,0.88rem)] font-medium text-slate-400">
                  <Store className="h-3 w-3" />
                  Head-to-head
                </span>
              )}
            >
              <div className="min-h-[7.5rem]">
                <StoreShareCompare
                  rows={storeDonutData}
                  metricLabel={activeMetric?.label || 'Sales'}
                  format={activeMetric?.format}
                />
              </div>
            </Panel>
          </div>

          <div className="grid grid-cols-12 gap-[clamp(0.5rem,0.8vw,0.85rem)]">
            <Panel className="col-span-7" title={`Weekly Heatmap · ${activeMetric?.label || 'Sales'}`}>
              {heatmapWeeks.length ? (
                <div className={`${DEEP_PANEL_H} overflow-auto`}>
                <table className="min-w-full border-collapse text-[clamp(0.95rem,1.1vw,1.05rem)]">
                  <thead>
                    <tr>
                      <th className="border border-slate-200 bg-slate-50 px-1.5 py-1 text-left font-semibold text-slate-600">Year</th>
                      <th className="border border-slate-200 bg-slate-50 px-1.5 py-1 text-left font-semibold text-slate-600">Week</th>
                      {(heatmap.weekdays || []).map((day) => (
                        <th key={day} className="border border-slate-200 bg-slate-50 px-1.5 py-1 font-semibold text-slate-600">
                          {day.slice(0, 3)}
                        </th>
                      ))}
                      <th className="border border-slate-200 bg-slate-50 px-1.5 py-1 font-semibold text-slate-600">Total</th>
                      <th className="border border-slate-200 bg-slate-50 px-1.5 py-1 font-semibold text-slate-600">Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapWeeks.map((week) => (
                      <tr key={week.key}>
                        <td className="border border-slate-200 px-1.5 py-1 font-medium text-slate-700">
                          {week.year || String(week.key || '').split('|')[0] || '—'}
                        </td>
                        <td className="border border-slate-200 px-1.5 py-1 font-medium text-slate-700">{week.label || week.week}</td>
                        {(heatmap.weekdays || []).map((day) => {
                          const value = Number(heatmap.cells?.[`${week.key}|${day}`] || 0);
                          const hot = value && value === heatmapMax;
                          return (
                            <td
                              key={`${week.key}-${day}`}
                              className={`border border-slate-200 px-1.5 py-1 text-center tabular-nums ${
                                hot ? 'font-bold text-white' : 'text-slate-800'
                              }`}
                              style={{ backgroundColor: heatmapColor(value, heatmapMax) }}
                            >
                              {value ? formatHeatValue(value) : '—'}
                            </td>
                          );
                        })}
                        <td className="border border-slate-200 bg-slate-50 px-1.5 py-1 text-center font-semibold tabular-nums">
                          {formatHeatValue(heatmap.rowTotals?.[week.key] || 0)}
                        </td>
                        <td className="border border-slate-200 bg-slate-50 px-1.5 py-1 text-center font-semibold tabular-nums">
                          {formatHeatValue(heatmap.rowAverages?.[week.key] || 0)}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={2} className="border border-slate-200 bg-slate-100 px-1.5 py-1 font-bold text-slate-800">Total</td>
                      {(heatmap.weekdays || []).map((day) => (
                        <td key={`total-${day}`} className="border border-slate-200 bg-slate-100 px-1.5 py-1 text-center font-bold tabular-nums text-slate-800">
                          {formatHeatValue(heatmap.colTotals?.[day] || 0)}
                        </td>
                      ))}
                      <td className="border border-slate-200 bg-slate-100 px-1.5 py-1 text-center font-bold tabular-nums text-slate-900">
                        {formatHeatValue(heatmap.grandTotal || 0)}
                      </td>
                      <td className="border border-slate-200 bg-slate-100 px-1.5 py-1 text-center font-bold tabular-nums text-slate-900">
                        {formatHeatValue(
                          heatmapWeeks.length
                            ? (heatmap.grandTotal || 0) / heatmapWeeks.length
                            : 0
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="border border-slate-200 bg-slate-50 px-1.5 py-1 font-bold text-slate-700">Avg</td>
                      {(heatmap.weekdays || []).map((day) => (
                        <td key={`avg-${day}`} className="border border-slate-200 bg-slate-50 px-1.5 py-1 text-center font-semibold tabular-nums text-slate-700">
                          {formatHeatValue(heatmap.colAverages?.[day] || 0)}
                        </td>
                      ))}
                      <td className="border border-slate-200 bg-slate-50 px-1.5 py-1 text-center font-semibold tabular-nums text-slate-800">
                        {formatHeatValue(
                          heatmapWeeks.length
                            ? heatmapWeeks.reduce((sum, week) => sum + Number(heatmap.rowTotals?.[week.key] || 0), 0) / heatmapWeeks.length
                            : 0
                        )}
                      </td>
                      <td className="border border-slate-200 bg-slate-50 px-1.5 py-1 text-center font-semibold tabular-nums text-slate-800">
                        {formatHeatValue(heatmap.grandAverage || 0)}
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No weekly heatmap data in this range.</p>
              )}
            </Panel>

            <Panel className="col-span-5" title={`By Location · ${activeMetric?.label || 'Sales'}`}>
              <div className={`${DEEP_PANEL_H} min-h-0`}>
                <ScrollableBarList
                  variant="inline"
                  rows={locationRows}
                  formatValue={(v) => formatMetricValue(v, activeMetric?.format)}
                  barColor={PURPLE}
                  emptyText="No location data in this range."
                  maxVisible={10}
                />
              </div>
            </Panel>
          </div>

          <div className="grid grid-cols-2 gap-[clamp(0.5rem,0.8vw,0.85rem)]">
            <Panel title={`By Category · ${activeMetric?.label || 'Sales'}`}>
              <ScrollableBarList
                variant="inline"
                rows={categoryBarRows}
                formatValue={(v) => formatMetricValue(v, activeMetric?.format)}
                barColor={BLUE}
                emptyText="No category data in this range."
                maxVisible={9}
              />
            </Panel>

            <Panel title={`By Product · ${activeMetric?.label || 'Sales'}`}>
              <ScrollableBarList
                variant="inline"
                rows={productBarRows}
                formatValue={(v) => formatMetricValue(v, activeMetric?.format)}
                barColor="#6366f1"
                emptyText="No product data in this range."
                maxVisible={9}
              />
            </Panel>
          </div>

          <Panel
            title={`Peak Hour · ${activeMetric?.label || 'Orders'}`}
            action={(
              <div className="flex items-center gap-2">
                {peakHighlight ? (
                  <span className="text-[clamp(1.05rem,1.2vw,1.15rem)] text-slate-600">
                    Peak <span className="font-bold text-slate-900">{peakHighlight.label}</span>
                    {' · '}
                    {formatMetricValue(peakHighlight.value ?? peakHighlight.orders, activeMetric?.format)}
                  </span>
                ) : null}
                <select
                  value={peakMode}
                  onChange={(e) => setPeakMode(e.target.value)}
                  className="input py-0.5 text-sm"
                  aria-label="Peak hours mode"
                >
                  <option value="average">Average</option>
                  <option value="daily">Daily</option>
                </select>
              </div>
            )}
          >
            <div className="h-[clamp(10rem,20vh,13.5rem)] min-w-0">
              {peakChartPoints.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={peakChartPoints} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="4 4" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="label"
                      stroke="#94a3b8"
                      tick={chartAxisTick}
                      interval={0}
                      minTickGap={8}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      tick={chartAxisTick}
                      tickFormatter={yTickFormatter}
                      axisLine={false}
                      tickLine={false}
                      width={52}
                    />
                    <Tooltip
                      contentStyle={chartTooltipStyle}
                      formatter={(value) => [formatMetricValue(value, activeMetric?.format), activeMetric?.label || 'Value']}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {peakChartPoints.map((entry) => (
                        <Cell
                          key={entry.label}
                          fill={entry.label === peakHighlight?.label ? PURPLE : '#c4b5fd'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="flex h-full items-center text-sm text-gray-500">No peak-hour data in this range.</p>
              )}
            </div>
          </Panel>
        </>
      ) : null}
    </div>
  );
};

export default OverviewDashboard;
